import React, { useEffect, useMemo, useState } from 'react';
import { WithHiddenListBox } from '@components/design';
import { FocusArea, KpiOptionForValue, KpiTypesOption, Props } from '../Types';
import { BEST_SELLERS_KPI_OPTION, HIGHEST_GROWTH_KPI_OPTION, SaleType } from '@models/Products';
import { useSelector } from 'react-redux';
import { IRootState } from '@state/reducers/Reducers';
import { isPeriodYTDOrMAT } from '@utils/PeriodUtils';

const ALL_KPI_OPTIONS = [BEST_SELLERS_KPI_OPTION, HIGHEST_GROWTH_KPI_OPTION];

const ProductsTop5TableFilter: React.FC<Props> = ({
  selectedKpi,
  selectedFocusArea,
  selectedSaleType,
  setSelectedKpi,
  setSelectedFocusArea,
  setSelectedSaleType,
  isLoading,
}) => {
  const [updatedKpi, setUpdatedKpi] = useState<string>(selectedKpi.value);
  const { period } = useSelector((state: IRootState) => state.mainFiltersProducts);

  const showKpiOptions = !isPeriodYTDOrMAT(period) ? ALL_KPI_OPTIONS : [BEST_SELLERS_KPI_OPTION];

  const focusAreaOptions = [
    {
      label: 'Market',
      value: FocusArea.market,
    },
    {
      label: 'Retailer',
      value: FocusArea.retailer,
    },
  ];

  const saleTypeOptions = [
    {
      label: 'Revenue',
      value: SaleType.revenue,
    },
    {
      label: 'Units',
      value: SaleType.volume,
    },
  ];

  const switchKpi = (option: KpiTypesOption | null) => {
    if (option) {
      setSelectedKpi(option);
    }
  };

  useEffect(() => {
    const selectedOption = ALL_KPI_OPTIONS.find((o) => o.value === updatedKpi);
    if (selectedOption) {
      switchKpi(selectedOption);
    }
  }, [updatedKpi]);

  const getOptionLabel = (options: any[], value: string) => {
    const option = options.find((opt) => opt.value === value);
    return option ? option.label : null;
  };

  const memoizedSelectedShowLabelOption = useMemo(
    () => getOptionLabel(showKpiOptions, updatedKpi),
    [showKpiOptions, updatedKpi],
  );
  const memoizedSelectedByLabelOption = useMemo(
    () => getOptionLabel(focusAreaOptions, selectedFocusArea),
    [focusAreaOptions, selectedFocusArea],
  );
  const memoizedSelectedAndByLabelOption = useMemo(
    () => getOptionLabel(saleTypeOptions, selectedSaleType),
    [saleTypeOptions, selectedSaleType],
  );

  return (
    <div className="flex-row flex-wrap items-center pb-4" data-testid="products-top-5-filter">
      <div>
        <WithHiddenListBox
          isDisabled={isLoading}
          label={'Show'}
          options={showKpiOptions}
          value={memoizedSelectedShowLabelOption ?? KpiOptionForValue.bestSellers}
          onChange={({ value }) => {
            setUpdatedKpi(value);
          }}
        />
      </div>
      <div className="flex-row gap-4" data-testid="products-top-5-filter-focusarea">
        <WithHiddenListBox
          isDisabled={isLoading}
          label={'by'}
          options={focusAreaOptions}
          value={memoizedSelectedByLabelOption ?? 'Market'}
          onChange={({ value }) => {
            setSelectedFocusArea(value as FocusArea);
          }}
        />
      </div>
      <div className="flex-row gap-4" data-testid="products-top-5-filter-saletype">
        <WithHiddenListBox
          isDisabled={isLoading}
          label="and by"
          options={saleTypeOptions}
          value={memoizedSelectedAndByLabelOption ?? 'Revenue'}
          onChange={({ value }) => {
            setSelectedSaleType(value as SaleType);
          }}
        />
      </div>
    </div>
  );
};

export default ProductsTop5TableFilter;
